import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { useFetchPickupAddressByUID, useUpdatePickupAddress } from '@/hooksApi/usePickupAddress';
import { parsePhoneNumber } from '@/utils';
import { PickupTypeEnum } from '@/types';
import LocationForm, { buildPayload } from './LocationForm';
import type { LocationFormState } from './LocationForm';

const SettingsLocationEdit = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [prefilled, setPrefilled] = useState<LocationFormState | undefined>(undefined);

  const { data: singleAddress, isFetching, isError } = useFetchPickupAddressByUID(id ?? '', !!id);

  const { mutate: updateAddress, isLoading: isUpdating } = useUpdatePickupAddress();

  /* Prefill form once data arrives */
  useEffect(() => {
    if (singleAddress?.object) {
      const obj = singleAddress.object;
      const phoneInfo = parsePhoneNumber(obj.phone);
      const provinceCode = obj.region?.province_code ?? '';
      setPrefilled({
        full_name: obj.full_name ?? '',
        countryCode: phoneInfo.countryCode ?? '964',
        phone: phoneInfo.phone ?? '',
        title: obj.title ?? '',
        type: obj.type ?? PickupTypeEnum.WAREHOUSE,
        province: provinceCode,
        region_uid: obj.region?.uid ?? '',
        address_text: obj.address_text ?? '',
        lat: obj.lat ? +obj.lat : 33.3152,
        lng: obj.lng ? +obj.lng : 44.3661,
      });
    }
  }, [singleAddress]);

  const handleSave = (form: LocationFormState) => {
    if (!id) return;
    const payload = buildPayload(form);
    updateAddress(
      { uid: id, data: payload },
      {
        onSuccess: () => {
          toast({ title: t('success_title'), description: t('operation') });
          queryClient.invalidateQueries(['pickupAddresses']);
          navigate('/settings/locations');
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.message || error?.message || t('operation_failed');
          toast({ title: t('error_title'), description: msg, variant: 'destructive' });
        },
      },
    );
  };

  /* Loading state */
  if (isFetching && !prefilled) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 max-w-3xl w-full mx-auto'>
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
            <p className='text-sm text-sd-muted-foreground'>{t('loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  /* Error / not found state */
  if (isError || (!isFetching && !singleAddress?.object)) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 max-w-3xl w-full mx-auto'>
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
            <MapPin className='h-10 w-10 mx-auto text-sd-muted-foreground mb-3' />
            <p className='text-sm text-sd-muted-foreground'>{t('no_data')}</p>
            <button
              onClick={() => navigate('/settings/locations')}
              className='mt-4 px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {t('back')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LocationForm
      heading={t('edit_location')}
      initialForm={prefilled}
      isSaving={isUpdating}
      onSave={handleSave}
      isEditMode={true}
    />
  );
};

export default SettingsLocationEdit;
